import { woocomerce } from '../utils/env';

const gogBalanceInFlight = new Map<string, Promise<GogBalanceResponse>>();
const gogBalanceCache = new Map<string, { ts: number; data: GogBalanceResponse }>();
const GOG_BALANCE_CACHE_TTL_MS = 15_000;

export type GGoGWithdrawalRequest = {
  walletAddress: string;
  amount: number;
};

export type GGoGWithdrawalResponse = {
  success: boolean;
  message: string;
  orderId: string;
  walletAddress: string;
  orderStatus: string;
  orderExchangeRate: number;
  orderAmount: number;
  orderAmountGOG: number | null;
  orderFeeGOG: number | null;
  orderCreateAt: number;
  orderRemaininggGoG: number | null;
};

export const gGoG2GoGApi = async (
  payload: GGoGWithdrawalRequest
): Promise<GGoGWithdrawalResponse> => {
  if (payload.walletAddress) payload.walletAddress = payload.walletAddress.toLocaleLowerCase();

  const response = await fetch(`${woocomerce}/wp-json/wc/v3/primary-sales/g-gog/withdraw`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    try {
      const errorBody = await response.text();
      if (errorBody) {
        errorMessage += `: ${errorBody}`;
      }
    } catch {
      // ignore parse error
    }
    throw new Error(errorMessage);
  }

  const data = (await response.json()) as GGoGWithdrawalResponse;
  return data;
};

export type GGoGWithdrawalFeeResponse = {
  success: boolean;
  message: string;
  quote: GGoGWithdrawalFeeQuoteResponse;
};

export type GGoGWithdrawalFeeQuoteResponse = {
  feeGog: string;
};

export const gGoGFeeApi = async (amount: number): Promise<GGoGWithdrawalFeeResponse> => {
  const response = await fetch(
    `${woocomerce}/wp-json/wc/v3/primary-sales/g-gog/withdraw/fee?amount=${amount}`,
    {
      method: 'get',
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    try {
      const errorBody = await response.text();
      if (errorBody) {
        errorMessage += `: ${errorBody}`;
      }
    } catch {
      // ignore parse error
    }
    throw new Error(errorMessage);
  }

  const data = (await response.json()) as GGoGWithdrawalFeeResponse;
  return data;
};

export type GGoGWithdrawalGgogBalanceResponse = {
  success: boolean;
  message: string;
  walletAddress: string;
  ggogBalance: number;
};

export const gGoGBalanceApi = async (
  walletAddress: string
): Promise<GGoGWithdrawalGgogBalanceResponse> => {
  const normalizedWalletAddress = walletAddress?.toLocaleLowerCase?.() ?? '';

  const response = await fetch(
    `${woocomerce}/wp-json/wc/v3/primary-sales/g-gog/withdraw/ggog-balance?walletAddress=${encodeURIComponent(
      normalizedWalletAddress
    )}`,
    {
      method: 'get',
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    try {
      const errorBody = await response.text();
      if (errorBody) {
        errorMessage += `: ${errorBody}`;
      }
    } catch {
      // ignore parse error
    }
    throw new Error(errorMessage);
  }

  const data = (await response.json()) as GGoGWithdrawalGgogBalanceResponse;
  return data;
};

export type GogBalanceResponse = {
  rawBalance: string;
  decimals: number;
  formattedBalance: string;
};

export const getCurrentUserGogBalance = async (params: {
  walletAddress: string;
  forceRefresh?: boolean;
}): Promise<GogBalanceResponse> => {
  const { walletAddress, forceRefresh } = params;

  if (!walletAddress) {
    throw new Error('walletAddress is required to query token balance');
  }

  const normalizedWalletAddress = walletAddress?.toLocaleLowerCase?.() ?? '';

  if (forceRefresh) {
    gogBalanceCache.delete(normalizedWalletAddress);
    gogBalanceInFlight.delete(normalizedWalletAddress);
  }

  const cached = gogBalanceCache.get(normalizedWalletAddress);
  if (cached && Date.now() - cached.ts < GOG_BALANCE_CACHE_TTL_MS) {
    return cached.data;
  }

  const existingRequest = gogBalanceInFlight.get(normalizedWalletAddress);
  if (existingRequest) {
    return existingRequest;
  }

  const req = (async () => {
    try {
      const response = await fetch(
        `${woocomerce}/wp-json/wc/v3/primary-sales/g-gog/explorer/gog-balance?walletAddress=${encodeURIComponent(
          normalizedWalletAddress
        )}`,
        {
          method: 'get',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        let errorMessage = `Request failed with status ${response.status}`;
        try {
          const errorBody = await response.text();
          if (errorBody) {
            errorMessage += `: ${errorBody}`;
          }
        } catch {
          // ignore parse error
        }
        throw new Error(errorMessage);
      }

      const data = (await response.json()) as GogBalanceResponse;
      gogBalanceCache.set(normalizedWalletAddress, { ts: Date.now(), data });
      return data;
    } finally {
      gogBalanceInFlight.delete(normalizedWalletAddress);
    }
  })();

  gogBalanceInFlight.set(normalizedWalletAddress, req);
  return req;
};

export const getGGoGWithdrawalFeeGog = async (amount: number): Promise<string | undefined> => {
  try {
    const feeRes = await gGoGFeeApi(amount);
    return feeRes?.quote?.feeGog;
  } catch {
    return undefined;
  }
};
